import { alpha, Alert, Box, Button, Card, CardContent, Chip, Grid, LinearProgress, Link, Stack, Tooltip, Typography } from '@mui/material';
import { AutoAwesomeRounded, CheckCircleRounded, RadioButtonUncheckedRounded, WarningAmberRounded } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import AssistantPanel from '@/components/assistant/AssistantPanel';
import Can from '@/components/common/Can';
import DataState from '@/components/common/DataState';
import PageHeader from '@/components/common/PageHeader';
import { useNotify } from '@/components/common/useNotify';
import type { SetupGuide, SetupStep } from '@/api/setupGuide.api';
import { useSetupGuide, useSkipSetupStep } from '@/hooks/useSetupGuide';
import { formatDate } from '@/utils/format';
import { useQuery } from '@tanstack/react-query';
import { APPROVALS_KEY, approvalsApi } from '@/api/vendors.api';

/**
 * Home (Module 0, O11): welcome with the trial, the trial banner, the guided setup checklist (one real entry
 * per module, in the order a first quote needs them), "Why this order" and the AI assistant.
 */
export default function DashboardPage() {
  const { profile } = useAuth();
  const guide = useSetupGuide();
  const g = guide.data;

  const subtitle = g ? [g.companyName, trialSummary(g)].filter(Boolean).join(' · ') : undefined;
  const firstName = profile?.firstName?.toUpperCase();

  return (
    <Box>
      <PageHeader
        title={firstName ? `Welcome, ${firstName}` : 'Welcome'}
        subtitle={subtitle}
        breadcrumbs={[{ label: 'Home' }]}
      />

      {g && <TrialBanner guide={g} />}
      <ApprovalsCard />

      <DataState compact loading={guide.isLoading} loadingText="Loading your setup…" error={guide.error} onRetry={guide.refetch}>
        {g && (
          <Grid container spacing={2} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 8 }}>
              {g.completed ? (
                <Alert severity="success" icon={<CheckCircleRounded />}>
                  Guided setup is complete — every step has a real entry.
                </Alert>
              ) : (
                <GuidedSetupCard guide={g} />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={2}>
                <Card>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ mb: 1 }}>Why this order</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                      A quote needs a place, a hotel with a contract and transport. After these steps your first quote is
                      priced from your own data, and you have seen where everything goes.
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle1" fontWeight={700} component="h2">Ask the assistant</Typography>
                      <AutoAwesomeRounded color="primary" fontSize="small" />
                    </Stack>
                    <AssistantPanel />
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        )}
      </DataState>
    </Box>
  );
}

// ---- Trial ----

function daysText(n: number) {
  return `${n} ${n === 1 ? 'day' : 'days'} left`;
}

function trialSummary(g: SetupGuide): string | null {
  if (g.trial.status === 'Trial') return `trial: ${daysText(g.trial.daysLeft)}`;
  if (g.trial.status === 'Ended') return 'trial ended';
  return null;
}

function TrialBanner({ guide }: { guide: SetupGuide }) {
  const { trial } = guide;
  if (trial.status !== 'Trial' && trial.status !== 'Ended') return null;
  const ended = trial.status === 'Ended';
  const kyc = guide.steps.find((s) => s.key === 'kyc');
  // Once sent, there is nothing to start: it is waiting for TravelERP. Sent back, it needs another look.
  const kycSent = trial.kycStatus === 'Submitted';
  const kycBack = trial.kycStatus === 'Rejected';
  const kycLink = trial.kycApproved || !kyc?.available ? null : kycSent ? (
    <> <strong>KYC submitted · waiting for approval.</strong></>
  ) : (
    <>
      {' '}
      <Link component={RouterLink} to={kyc.path} fontWeight={600} underline="hover">{kycBack ? 'KYC sent back · review it' : 'Start KYC'}</Link>
    </>
  );

  return (
    <Alert
      severity={ended ? 'error' : 'warning'}
      icon={<WarningAmberRounded />}
      sx={{ mb: 2.5, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
    >
      <strong>{ended ? 'Trial ended.' : `Trial · ${daysText(trial.daysLeft)}.`}</strong>{' '}
      {ended
        ? `Your free trial ended on ${formatDate(trial.endsAt, 'D MMM YYYY')}. Contact us to choose a plan or ask for more time.`
        : 'Everything works for practice.'}
      {!trial.kycApproved && ' Real invoices, payment links and vendor invites unlock after KYC is approved.'}
      {kycLink}
    </Alert>
  );
}

// ---- Guided setup ----

function GuidedSetupCard({ guide }: { guide: SetupGuide }) {
  const pct = guide.total ? Math.round((guide.doneCount / guide.total) * 100) : 0;
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle1" fontWeight={700} component="h2">
          Guided setup · {guide.doneCount} of {guide.total} done
        </Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          aria-label={`${pct}% of guided setup done`}
          sx={{ height: 8, borderRadius: 4, my: 1.75 }}
        />
        <Stack spacing={1.25}>
          {guide.steps.map((s, i) => (
            <StepRow
              key={s.key}
              step={s}
              primary={!s.available || s.status === 'Done' ? false : guide.steps.findIndex((x) => x.available && x.status === 'Todo') === i}
              channels={s.key === 'channels' ? guide.channels : undefined}
            />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.75, lineHeight: 1.6 }}>
          Add at least one real entry in each step; each opens the real screen with tips. Skip any step and come
          back; this list stays on Home until done.
        </Typography>
      </CardContent>
    </Card>
  );
}

function StepRow({ step, primary, channels }: { step: SetupStep; primary: boolean; channels?: SetupGuide['channels'] }) {
  const navigate = useNavigate();
  const notify = useNotify();
  const skip = useSkipSetupStep();
  const done = step.status === 'Done';
  const skipped = step.status === 'Skipped';

  const open = () => navigate(`${step.path}?guide=${encodeURIComponent(step.key)}`);
  const toggleSkip = async () => {
    try {
      await skip.mutateAsync({ key: step.key, skip: !skipped });
    } catch (e) {
      notify.error(e);
    }
  };

  return (
    <Box
      sx={(t) => ({
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1.25, sm: 1.5 },
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        border: 1,
        borderRadius: 2,
        borderColor: done ? alpha(t.palette.success.main, 0.35) : 'divider',
        bgcolor: done ? alpha(t.palette.success.main, 0.06) : 'background.paper',
      })}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flexGrow: 1 }}>
        {done ? (
          <CheckCircleRounded color="success" sx={{ flexShrink: 0 }} />
        ) : (
          <RadioButtonUncheckedRounded sx={{ color: 'text.disabled', flexShrink: 0 }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
            {step.title}
          </Typography>
          {channels && !done && step.available && (
            <Typography variant="caption" color="text.secondary">
              Email test {channels.emailTested ? '✓ sent' : 'not sent yet'} · WhatsApp test{' '}
              {channels.whatsAppTested ? '✓ sent' : 'not sent yet'}
            </Typography>
          )}
        </Box>
        {skipped && <Chip size="small" label="Skipped" variant="outlined" />}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: { sm: 0.5 } }}>
          {step.minutes} min
        </Typography>
        {done ? null : step.available ? (
          <>
            <Can perm="Company.Edit">
              <Button size="small" color="inherit" onClick={toggleSkip} disabled={skip.isPending}>
                {skipped ? 'Resume' : 'Skip'}
              </Button>
            </Can>
            <Button size="small" variant={primary ? 'contained' : 'outlined'} onClick={open} sx={{ ml: { xs: 'auto', sm: 0 } }}>
              Start
            </Button>
          </>
        ) : (
          <Tooltip title="Opens when this module is switched on for your company.">
            {/* The span keeps the tooltip working on a disabled button. */}
            <span style={{ marginLeft: 'auto' }}>
              <Button size="small" variant="outlined" disabled>Start</Button>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

/**
 * FR-NAV-07/08: approvals waiting always reach the admin on Home, without opening any menu (vendor KYC, rate changes).
 */
function ApprovalsCard() {
  const { hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const canApprove = hasAnyPermission(['Vendor.Approve']);
  const list = useQuery({ queryKey: [APPROVALS_KEY, 'list'], queryFn: approvalsApi.list, enabled: canApprove, staleTime: 60_000 });
  const items = list.data?.items ?? [];
  if (!canApprove || items.length === 0) return null;
  return (
    <Card sx={{ mb: 2, borderColor: 'warning.light' }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <WarningAmberRounded color="warning" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ flex: 1 }}>Approvals waiting ({items.length})</Typography>
          <Button variant="contained" size="small" onClick={() => navigate('/approvals')}>Open Approvals centre</Button>
        </Stack>
        <Stack spacing={0.5}>
          {items.slice(0, 5).map((i) => (
            <Typography key={`${i.kind}-${i.id}`} variant="body2"><b>{i.title}</b> · {i.subtitle}</Typography>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
